import { useEffect, useState, useContext } from 'react'
import { Delete, Edit, FolderOutlined } from '@mui/icons-material'
import { CircularProgress, TableCell, TableRow, Checkbox, Tooltip, Fab } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { motion } from 'framer-motion'
import { SquareDashedKanban as HeaderIcon } from 'lucide-react'
import moment from 'moment'
import { useSelector, useDispatch } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { Paper, CvsDrawer } from '@components'
import { AdminHeader } from '@components/layout/admin-header'
import { modalContext } from '@contexts'
import { colors } from '@global/constants'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { AxiosError } from 'axios'
import { showSnackbar } from 'functions'
import { EditFolder } from './edit-folder'
import { updateFolder, addFolder, getFolders, updateFolders } from './folders.actions'
import { CvsTable } from '../../../components/table/cvs-table'
import { EnhancedTableToolbar } from '../../../components/table/enhanced-table-toolbar'
import { Fuzzy } from '../../../utils/fuse-search'
import { t } from '@lingui/core/macro'

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '100%',
    height: '100%',
  },
  paper: {
    width: '100%',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 0,
  },
  table: {
    height: '100%',
    width: '100%',
    tableLayout: 'fixed',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  textField: {
    width: 200,
  },
}))

function getCellContent(type, value) {
  switch (type) {
    case 'number':
      return Intl.NumberFormat().format(value)
    case 'date':
      return value ? moment(value).format('MM/DD/YY') : t`Never`
    case 'list':
      return value.join(', ')
    default:
      return value
  }
}

export const FolderList = () => {
  const columns = [
    { label: t`Name`, width: '40%', lookup: 'name' },
    { label: t`Updated Date`, type: 'date', width: '20%', lookup: 'updated_at' },
    { label: t`Description`, width: '40%', lookup: 'description' },
  ]

  const classes = useStyles()
  const ModalContext = useContext(modalContext)
  const [visible, setVisible] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [selected, setSelected] = useState({})
  const [displayFolders, setDisplayFolders] = useState([])
  const dispatch = useDispatch()
  const [selectedFolder, setSelectedFolder] = useState(null)
  const [search, setSearch] = useState('')
  const [DATASETS_ENABLED = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.DATASETS_PAGE]),
  )

  const folders = useSelector(state => state.Folders.folders)
  const loading = useSelector(state => state.Folders.loading)

  const onClose = () => {
    setVisible(false)
    setSelectedFolder(null)
  }

  const fuzzySearch = searchTerm => {
    const fuzzy = new Fuzzy(folders, { keys: ['name'] })
    return fuzzy.search(searchTerm)
  }

  useEffect(() => {
    dispatch(getFolders())
  }, [])

  useEffect(() => {
    if (!loading) {
      setLoaded(true)
      setDisplayFolders(search ? fuzzySearch(search) : folders)
    }
  }, [folders, search, loading])

  function editFolder(folder) {
    setVisible(true)
    setSelectedFolder(folder)
  }

  const editItems = async () => {
    const item = Object.keys(selected).find(key => selected[key] === true)
    setSelectedFolder(displayFolders.find(f => f.id === Number(item)))
    setVisible(true)
  }

  const addItem = () => {
    setSelectedFolder({ name: '', description: '' })
    setVisible(true)
  }

  const deleteItems = payload => {
    // move to redux action creator
    api
      .post('/api/v1/account/folder/bulk/delete', {
        folder_ids: Object.keys(payload).filter(p => payload[p]),
      })
      .then(() => {
        const filteredFolders = folders.filter(f => !payload[f.id])
        setDisplayFolders(filteredFolders)
        dispatch(updateFolders(filteredFolders))
        setSelected({})
        dispatch(
          showSnackbar({
            message: 'Folder deleted successfully',
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(e => {
        const message =
          e instanceof AxiosError && e.status === 400
            ? e.response.data.message
            : 'Folder failed to delete'
        dispatch(
          showSnackbar({
            message: message,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const deleteItemsModal = () => {
    ModalContext.displayModal({
      title: 'Are you sure you want to delete these folders?',
      description: `Folders are a key way to organize and share ${
        DATASETS_ENABLED ? 'datasets' : 'templates'
      }. Any ${
        DATASETS_ENABLED ? 'datasets' : 'templates'
      } contained within these folders will be moved to an "Unassigned ${
        DATASETS_ENABLED ? 'Datasets' : 'Templates'
      }" folder.`,
      confirm: () => {
        return () => {
          deleteItems(selected)
        }
      },
    })
  }

  const numSelected = Object.values(selected).filter(val => !!val)?.length || 0

  return (
    <motion.div
      className={classes.root}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      style={{ overflow: 'hidden' }}
      data-test-page-id="admin-folders"
    >
      <AdminHeader>
        <AdminHeader.PageIcon>
          <HeaderIcon color="hsla(194, 47%, 20%, 1)" size={28} />
        </AdminHeader.PageIcon>
        <div className="ml-4">
          <AdminHeader.Title>{t`Folders`}</AdminHeader.Title>
          <AdminHeader.Subtitle>
            {`A collection of submission ${
              DATASETS_ENABLED ? 'datasets' : 'templates'
            } that can be easily shared`}
          </AdminHeader.Subtitle>
        </div>
      </AdminHeader>
      <Paper elevation={2} className={classes.paper}>
        {loaded ? (
          <div
            className={classes.table}
            style={{ height: 'calc(100% - 85px)' }}
            data-test-cmp-id="admin-folders-container"
          >
            <EnhancedTableToolbar
              term={search}
              numSelected={numSelected}
              filterChanged={setSearch}
              addItem={addItem}
              editItems={editItems}
              deleteItems={deleteItems}
              noAdd={false}
              blockMultipleEdit={false}
            >
              <Tooltip title={t`Edit`}>
                <Fab
                  color="primary"
                  style={{ color: 'white', marginRight: '10px' }}
                  size="small"
                  aria-label="Edit"
                  disabled={numSelected > 1}
                  className={classes.fab}
                  onClick={editItems}
                >
                  <Edit />
                </Fab>
              </Tooltip>
              <Tooltip
                title={t`Delete`}
                style={{ backgroundColor: colors.red.light, color: 'white' }}
              >
                <Fab
                  color="inherit"
                  size="small"
                  aria-label="Add"
                  className={classes.fab}
                  onClick={deleteItemsModal}
                >
                  <Delete />
                </Fab>
              </Tooltip>
            </EnhancedTableToolbar>
            <div style={{ height: 'calc(100% - 64px)' }}>
              <CvsTable
                columns={columns}
                rows={displayFolders}
                orderBy="updated_at"
                sortOrder="desc"
                keyField="id"
                noAdd={false}
                addItem={addItem}
                noPagination
                style={{ maxWidth: '100%' }}
                multiSelect
                emptyStateMessage={{
                  icon: (
                    <Fab
                      style={{
                        height: '65px',
                        width: '65px',
                        color: colors.gray.light,
                        cursor: 'auto',
                      }}
                      disabled
                    >
                      <FolderOutlined style={{ fontSize: '34px', color: colors.blue.smokey }} />
                    </Fab>
                  ),
                  title: 'No Folders Found.',
                  description: t`Try a different search or add a new Folder.`,
                }}
                selectAll={val => {
                  setSelected(
                    displayFolders.reduce(
                      (memo, item) => ({
                        ...memo,
                        [item.id]: val,
                      }),
                      {},
                    ),
                  )
                }}
              >
                {({ row }) => {
                  return (
                    <TableRow
                      hover
                      onClick={() => {
                        editFolder(row)
                      }}
                      tabIndex={-1}
                      key={row.id}
                      style={{ cursor: 'pointer', maxWidth: '100%', overflow: 'hidden' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          onClick={e => {
                            e.stopPropagation()
                            setSelected({
                              ...selected,
                              [row.id]: !selected[row.id],
                            })
                          }}
                          checked={selected[row.id]}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getCellContent('text', row.name)}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {getCellContent('date', row.updated_at)}
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        padding="normal"
                        style={{
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {row.description}
                      </TableCell>
                    </TableRow>
                  )
                }}
              </CvsTable>
            </div>
            <CvsDrawer
              visible={visible}
              onClose={onClose}
              title={`Edit ${(selectedFolder && selectedFolder.name) || 'New Folder'}`}
              data-test-cmp-id="drawer-edit-folder"
            >
              <EditFolder
                folder={selectedFolder || {}}
                onSubmit={payload => {
                  return dispatch(
                    selectedFolder && selectedFolder.name
                      ? updateFolder(payload)
                      : addFolder(payload),
                  )
                }}
                onClose={onClose}
              />
            </CvsDrawer>
          </div>
        ) : (
          <div style={{ padding: 24, display: 'flex', width: '100%', justifyContent: 'center' }}>
            <CircularProgress color="primary" />
          </div>
        )}
      </Paper>
    </motion.div>
  )
}
